import { Injectable } from '@angular/core';
import { Observable, take } from 'rxjs';
import { CatalogSku } from 'src/app/common/CatalogSku';
import { ApiServiceBase } from './ApiServiceBase';
import { CatalogItem } from '../common/CatalogItem';
import { CatalogFilter } from '../common/CatalogFilter';
import { CatalogPins } from '../common/CatalogPins';
import { CatalogGeoQuery, geoToHttpParams, hasGeoArea } from '../common/Geo';
import { PagedResult, PagingParams, pagingToHttpParams } from '../common/PagedResult';

/** `sortBy` value of the distance order — only valid with a position (`sortByRequiresPosition`). */
export const CATALOG_SORT_BY_DISTANCE = 'distance';

@Injectable({
  providedIn: 'root',
})
export class CatalogService extends ApiServiceBase {
  protected readonly path = 'catalog';

  getById(id: string): Observable<CatalogSku> {
    return this.http
      .get<CatalogSku>(this.getUrl() + '/' + id)
      .pipe(take(1));
  }

  /**
   * The catalog search. With a complete geo area (backend #678 / web #172) it is the located
   * search: the result holds only sellers inside the area, every item carries `distanceKm` from
   * the origin, and — unless the caller sorts otherwise — the nearest comes first. The origin is
   * rounded to 2 decimals on the way out (decision 9); an incomplete area sends no geo parameters
   * at all, so the endpoint stays the plain search rather than answering a 400.
   */
  getBySearch(filter: CatalogFilter, paging?: PagingParams, geo?: CatalogGeoQuery | null): Observable<PagedResult<CatalogItem>> {
    const located = hasGeoArea(geo) && !!geo.origin;
    const effectivePaging: PagingParams | undefined =
      located && paging?.sortBy === undefined ? { ...paging, sortBy: CATALOG_SORT_BY_DISTANCE } : paging;

    return this.http
      .get<PagedResult<CatalogItem>>(this.getUrl() + '/catalogitems', {
        params: {
          ...this.filterParams(filter),
          ...geoToHttpParams(geo),
          ...pagingToHttpParams(effectivePaging),
        },
      });
  }

  /**
   * The map's pins for the same filter: one per seller over the ENTIRE result set, capped at
   * 500 (`truncated`). No paging — the endpoint ignores it — and the same geo rules as the list,
   * which is what lets the map re-query by viewport (`bounds`, with the origin as the distance
   * reference) while the list keeps its radius: both go through one candidate pipeline on the
   * backend, so they can never disagree about what matched.
   */
  getPins(filter: CatalogFilter, geo?: CatalogGeoQuery | null): Observable<CatalogPins> {
    return this.http.get<CatalogPins>(this.getUrl() + '/catalogitems/pins', {
      params: {
        ...this.filterParams(filter),
        ...geoToHttpParams(geo),
      },
    });
  }

  private filterParams(filter: CatalogFilter): Record<string, string | boolean> {
    return {
      name: filter.name ? filter.name : '',
      productCategoryId: filter.categoryId == null ? '' : filter.categoryId,
      // #509 (backend): also list zero-stock listings; each row carries an explicit
      // availability state and non-available rows render without an order action.
      includeUnavailable: true,
    };
  }
}
